'use client'

import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Globe, Loader2 } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { CopyChip } from '../ProcessedEntry/DomainEntry/sharedComponents'
import { apiGet } from '../Entries/entriesApi'
import { parseDbTimestamp } from '@/lib/dates'

// One user_entry_domain row as /entry-detail embeds it (getUserEntryDetail's
// `user_entry_domain(...)` select): the link row, its domain, and the whois
// snapshot(s) taken for it. entry_domain_data.raw_domain_metadata is the
// classifier's domain_metadata dict — domain / original_url / create_date /
// registrar / is_active / domainNameExt — plus, since e32 started stamping
// it, a `whois` block of registry facts parsed deterministically in Python
// (libs/tooling/dns.py parse_whois_fields): status, creation_date,
// expiration_date, registrar, owner_name, admin_name, name_servers, source.
export type EntryDomainLinkRow = {
  id: string
  original_url: string | null
  is_subdomain: boolean | null
  manual_inactive: boolean | null
  domain_id: string
  created_at: string
  domain: { url: string; domain_name_ext: string | null } | null
  entry_domain_data: Array<{ raw_domain_metadata: Record<string, any> | null; created_at: string }> | null
}

type PipelineStepRow = { step: string; created_at: string; json_info: any }

type LiveDomains = {
  user_entry_domain?: EntryDomainLinkRow[] | null
  user_entry_pipeline_steps?: PipelineStepRow[] | null
}

// What the panel shows per domain, whichever of the two sources it came from.
type WhoisView = {
  status?: string | null
  creation_date?: string | null
  expiration_date?: string | null
  updated_date?: string | null
  registrar?: string | null
  owner_name?: string | null
  admin_name?: string | null
  name_servers?: string[] | null
  source?: string | null
  raw_output?: string | null
}

function fmtDate(iso: string | null | undefined) {
  const d = parseDbTimestamp(iso ?? null)
  if (!d) return null
  return new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
}

// Whois dates are registry strings ("2013-01-07", "2020-03-12T12:42:09.000Z",
// the literal "null"), not DB timestamps, so they don't go through
// parseDbTimestamp (whose appended "Z" turns a bare "2013-07-01" into a
// string Chrome mis-parses). Only the ISO date prefix matters here.
function whoisDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value || value === 'null') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  const d = m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function fmtWhoisDate(value: unknown): string | null {
  const d = whoisDate(value)
  if (!d) return typeof value === 'string' && value && value !== 'null' ? value : null
  return new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(d)
}

function domainAge(createDate: unknown): string | null {
  const d = whoisDate(createDate)
  if (!d) return null
  const months = Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44)))
  if (months < 1) return 'este mês'
  if (months < 24) return `${months} ${months === 1 ? 'mês' : 'meses'}`
  return `${Math.floor(months / 12)} anos`
}

function latestWhois(row: EntryDomainLinkRow): Record<string, any> | null {
  const snapshots = (row.entry_domain_data ?? []).filter((s) => s.raw_domain_metadata)
  if (snapshots.length === 0) return null
  snapshots.sort((a, b) => (parseDbTimestamp(b.created_at)?.getTime() ?? 0) - (parseDbTimestamp(a.created_at)?.getTime() ?? 0))
  return snapshots[0].raw_domain_metadata
}

// Fallback for entries classified before Python persisted the parsed whois:
// the get_domain_info step in the newest classify_success trace still holds
// the registry's raw text (bash/socket sources) or JSON record, keyed by the
// registrable domain it queried. Parsed here the same way dns.py does for
// "Key: Value" text — first occurrence of a key wins (the registrant block
// precedes admin/tech), name servers collected as a list.
function whoisFromTrace(steps: PipelineStepRow[] | null | undefined): Map<string, WhoisView> {
  const out = new Map<string, WhoisView>()
  const successes = (steps ?? [])
    .filter((s) => s.step === 'classify_success' && s.json_info && Array.isArray(s.json_info.pipeline_trace))
    .sort((a, b) => (parseDbTimestamp(b.created_at)?.getTime() ?? 0) - (parseDbTimestamp(a.created_at)?.getTime() ?? 0))
  for (const s of successes) {
    for (const t of s.json_info.pipeline_trace as any[]) {
      if (t?.step_name !== 'get_domain_info' || !t.output || typeof t.output !== 'object') continue
      const output = t.output as Record<string, any>
      const domain = String(output.domain ?? '').toLowerCase()
      if (!domain || out.has(domain)) continue
      const view: WhoisView = { source: output.source ?? null }
      const raw = output.raw_output
      if (typeof raw === 'string') {
        view.raw_output = raw
        const fields = new Map<string, string>()
        const nameServers: string[] = []
        for (const line of raw.split('\n')) {
          const idx = line.indexOf(':')
          if (idx < 0) continue
          const key = line.slice(0, idx).trim().toLowerCase()
          const value = line.slice(idx + 1).trim()
          if (!value || key.startsWith('%') || key.startsWith('#')) continue
          if (['name server', 'nserver', 'nameserver', 'name servers'].includes(key)) {
            const host = value.split('|')[0].trim().split(/\s+/)[0]?.toLowerCase()
            if (host && !nameServers.includes(host)) nameServers.push(host)
            continue
          }
          if (!fields.has(key)) fields.set(key, value)
        }
        const pick = (...keys: string[]) => keys.map((k) => fields.get(k)).find((v) => v) ?? null
        view.status = pick('domain status', 'status')
        view.creation_date = pick('creation date', 'created', 'created on', 'registered on', 'registration date')
        view.expiration_date = pick('expiration date', 'expiry date', 'expires', 'registry expiry date', 'paid-till')
        view.registrar = pick('registrar', 'sponsoring registrar', 'registrar name')
        view.owner_name = pick('owner name', 'registrant name', 'registrant organization', 'registrant', 'holder', 'org', 'organization')
        view.admin_name = pick('admin name', 'administrative contact', 'admin contact')
        view.name_servers = nameServers.length ? nameServers : null
      } else if (raw && typeof raw === 'object') {
        view.creation_date = raw.createdDate ?? null
        view.expiration_date = raw.expiresDate ?? null
        view.registrar = raw.registrarName ?? null
        view.status = typeof raw.status === 'string' ? raw.status : null
        view.owner_name = raw.registrant?.organization ?? raw.registrant?.name ?? null
        view.admin_name = raw.administrativeContact?.organization ?? raw.administrativeContact?.name ?? null
        view.name_servers = Array.isArray(raw.nameServers?.hostNames) ? raw.nameServers.hostNames : null
      }
      out.set(domain, view)
    }
  }
  return out
}

const isBlank = (v: unknown) => v == null || v === '' || v === 'null' || v === 'N/A'

/**
 * Every domain linked to a user entry, newest first, each with its whois:
 * registry status, owner, admin, registrar, created/expires, name servers.
 * Self-contained (fetches /entry-detail by user_entry_id) so any detail page
 * can drop it in — the SIBS entry page had nowhere to show a domain at all,
 * even once the classifier had linked one (BR-654), and with the router now
 * following every domain in an entry (a link in the body AND the sender's
 * domain) there is usually more than one row.
 *
 * `refreshKey` re-fetches when it changes (e.g. after a Reclassify run).
 */
export function EntryDomainsWidget({ userEntryId, theme, refreshKey }: {
  userEntryId: string
  theme: ProcessedTheme
  refreshKey?: string | number
}) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'
  const [rows, setRows] = useState<EntryDomainLinkRow[] | null>(null)
  const [traceWhois, setTraceWhois] = useState<Map<string, WhoisView>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rawOpen, setRawOpen] = useState<Record<string, boolean>>({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    apiGet<{ entry: LiveDomains | null }>(`/entry-detail?user_entry_id=${encodeURIComponent(userEntryId)}`)
      .then((result) => {
        if (cancelled) return
        if (!result.success) {
          setError(typeof result.error === 'string' ? result.error : 'Could not load domains')
          setRows([])
          return
        }
        const list = [...(result.data?.entry?.user_entry_domain ?? [])]
        list.sort((a, b) => (parseDbTimestamp(b.created_at)?.getTime() ?? 0) - (parseDbTimestamp(a.created_at)?.getTime() ?? 0))
        setRows(list)
        setTraceWhois(whoisFromTrace(result.data?.entry?.user_entry_pipeline_steps))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [userEntryId, refreshKey])

  if (loading && rows === null) {
    return (
      <div className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>
        <Loader2 className="h-3 w-3 animate-spin" /> Loading domains…
      </div>
    )
  }
  if (error) {
    return <div className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>{error}</div>
  }
  if (!rows || rows.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center gap-1 py-6 text-center ${c.faint}`}>
        <Globe className="h-4 w-4" />
        <div className="font-mono text-[10px] uppercase tracking-widest">No domains linked to this entry</div>
        <div className="text-[11px]">The classifier links every domain it finds — in the message or in the sender address.</div>
      </div>
    )
  }

  const dt = `uppercase tracking-widest text-[9px] ${c.faint}`

  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => {
        const meta = latestWhois(row)
        const url = (row.domain?.url ?? meta?.domain ?? '—').toLowerCase()
        // Persisted parsed whois (new runs) first; the trace's get_domain_info
        // output for older runs; the compact domain_metadata as last resort.
        const persisted: WhoisView | null = meta?.whois && typeof meta.whois === 'object' ? meta.whois : null
        const fromTrace = traceWhois.get(url) ?? null
        const whois: WhoisView = {
          ...(fromTrace ?? {}),
          ...(persisted ?? {}),
          raw_output: fromTrace?.raw_output ?? null,
        }
        const created = whois.creation_date ?? meta?.create_date
        const age = domainAge(created)
        const registrar = !isBlank(whois.registrar) ? whois.registrar : !isBlank(meta?.registrar) ? meta?.registrar : null
        const isActive = meta?.is_active
        const inactive = row.manual_inactive === true || isActive === false
        const rawShown = rawOpen[row.id] === true
        return (
          <div key={row.id} className={`rounded border p-3 ${c.line} bg-card`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`font-mono text-[13px] font-semibold break-all ${c.text}`}>{url}</span>
              <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${
                inactive
                  ? ('bg-danger-surface text-destructive')
                  : ('bg-success-surface text-success')
              }`}>
                {row.manual_inactive ? 'manually inactive' : inactive ? 'inactive' : 'active'}
              </span>
              {row.is_subdomain && (
                <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest bg-secondary text-muted-foreground`}>subdomain</span>
              )}
            </div>
            {row.original_url && row.original_url.toLowerCase() !== url && row.original_url.toLowerCase() !== `https://${url}` && (
              <div className={`mt-1 font-mono text-[11px] break-all ${c.muted}`}>{row.original_url}</div>
            )}
            <dl className={`mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono text-[11px] ${c.muted}`}>
              {!isBlank(whois.status) && (<><dt className={dt}>status</dt><dd className="break-all">{whois.status}</dd></>)}
              {!isBlank(whois.owner_name) && (<><dt className={dt}>owner</dt><dd className="break-words">{whois.owner_name}</dd></>)}
              {!isBlank(whois.admin_name) && whois.admin_name !== whois.owner_name && (<><dt className={dt}>admin</dt><dd className="break-words">{whois.admin_name}</dd></>)}
              <dt className={dt}>registered</dt>
              <dd className="break-all">{fmtWhoisDate(created) ? `${fmtWhoisDate(created)}${age ? ` · ${age}` : ''}` : '—'}</dd>
              {!isBlank(whois.expiration_date) && (<><dt className={dt}>expires</dt><dd className="break-all">{fmtWhoisDate(whois.expiration_date) ?? whois.expiration_date}</dd></>)}
              <dt className={dt}>registrar</dt>
              <dd className="break-all">{registrar ?? '—'}</dd>
              {whois.name_servers && whois.name_servers.length > 0 && (
                <><dt className={dt}>name servers</dt><dd className="break-all">{whois.name_servers.join(', ')}</dd></>
              )}
              <dt className={dt}>linked</dt>
              <dd>{fmtDate(row.created_at) ?? '—'}{!isBlank(whois.source) ? <span className={c.faint}> · via {whois.source}</span> : null}</dd>
              <dt className={dt}>domain id</dt>
              <dd><CopyChip value={row.domain_id} display={row.domain_id.slice(0, 8)} /></dd>
            </dl>
            {whois.raw_output && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setRawOpen((prev) => ({ ...prev, [row.id]: !rawShown }))}
                  className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest ${c.faint} hover:underline`}
                >
                  {rawShown ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  raw whois
                </button>
                {rawShown && (
                  <pre className={`mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded border p-2 font-mono text-[10px] leading-snug ${c.line} ${c.muted}`}>{whois.raw_output}</pre>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
